'use client';

import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { useDisplayCurrency } from '@/tenants/_shared/hooks/useCurrencies';
import { type CurrencyOption } from '@impact/types';
import {
  type DonationStrategy,
  type DonationSessionInput,
  type ActivePaymentSession,
} from './types';
import { useSelfAttribution } from './useSelfAttribution';

const PAYMENT_CONFIG_CACHE_MS = 1000 * 60 * 5;

export function useGeneralDonationStrategy(
  campaignId: string | undefined,
  currencyCode: string | undefined,
): DonationStrategy {
  const displayCurrency = useDisplayCurrency();
  const selfAttribution = useSelfAttribution();

  const {
    data: paymentConfig,
    isLoading: isPaymentConfigLoading,
    isFetching: isPaymentConfigFetching,
    isError: isPaymentConfigError,
  } = trpc.publicPayment.getGeneralConfig.useQuery(
    { campaignId: campaignId!, currencyCode: currencyCode! },
    {
      enabled: !!campaignId && !!currencyCode,
      staleTime: PAYMENT_CONFIG_CACHE_MS,
      gcTime: PAYMENT_CONFIG_CACHE_MS * 2,
      refetchOnWindowFocus: false,
    },
  );

  // Derive currency from general config, falling back to display currency
  const currency: CurrencyOption = useMemo(() => {
    const pc = paymentConfig?.paymentConfigs?.[0];
    if (pc?.currencyCode && pc.currencySymbol && pc.currencyName) {
      return {
        code: pc.currencyCode,
        symbol: pc.currencySymbol,
        label: pc.currencyName,
      };
    }
    return displayCurrency;
  }, [paymentConfig, displayCurrency]);

  const createGeneralSession = trpc.publicDonation.createGeneralSession.useMutation();

  return useMemo(
    (): DonationStrategy => ({
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      isFundraisingActive: true,
      isSessionCreationPending: createGeneralSession.isPending,
      selfAttribution,
      checkoutScope: {
        scope: 'general' as const,
        campaignId: campaignId!,
        currencyCode: currencyCode!,
      },
      createSession: (input: DonationSessionInput): Promise<ActivePaymentSession> =>
        createGeneralSession.mutateAsync({
          ...input,
          campaignId: campaignId!,
          currencyCode: currencyCode!,
        }),
    }),
    [
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      selfAttribution,
      createGeneralSession,
      campaignId,
      currencyCode,
    ],
  );
}
